"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useMemo } from "react";
import { useSession } from "next-auth/react";
import { fetchJson } from "@/lib/client/fetch-json";
import { queryKeys } from "@/lib/client/query-keys";

type WishlistContextValue = {
  isInWishlist: (productId: string) => boolean;
  count: number;
  loading: boolean;
  toggleWishlist: (productId: string) => Promise<{ ok: boolean; inWishlist: boolean; unauthorized?: boolean }>;
};

const WishlistContext = createContext<WishlistContextValue | null>(null);

type WishlistItem = {
  id: string;
};

export function WishlistProvider({ children }: { children: React.ReactNode }) {
  const { status } = useSession();
  const queryClient = useQueryClient();

  const wishlistQuery = useQuery({
    queryKey: queryKeys.wishlist.ids,
    enabled: status === "authenticated",
    staleTime: 30 * 1000,
    queryFn: () =>
      fetchJson<WishlistItem[]>("/api/wishlist", {
        cache: "no-store",
        fallbackMessage: "Не вдалося завантажити обране",
      }),
    select: (data) => {
      const next = new Set<string>();
      (Array.isArray(data) ? data : []).forEach((item) => {
        if (item?.id) next.add(item.id);
      });
      return next;
    },
  });

  const wishlistIds = useMemo(() => {
    if (status !== "authenticated") {
      return new Set<string>();
    }

    return wishlistQuery.data ?? new Set<string>();
  }, [status, wishlistQuery.data]);

  const toggleWishlistMutation = useMutation({
    mutationFn: async (payload: { productId: string; currentlyInWishlist: boolean }) => {
      const { productId, currentlyInWishlist } = payload;

      if (currentlyInWishlist) {
        const res = await fetch(`/api/wishlist?productId=${productId}`, { method: "DELETE" });
        if (res.status === 401) {
          return { ok: false, inWishlist: false, unauthorized: true };
        }
        if (!res.ok) {
          return { ok: false, inWishlist: true };
        }

        return { ok: true, inWishlist: false };
      }

      const res = await fetch("/api/wishlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId }),
      });

      if (res.status === 401) {
        return { ok: false, inWishlist: false, unauthorized: true };
      }
      if (!res.ok) {
        return { ok: false, inWishlist: false };
      }

      return { ok: true, inWishlist: true };
    },
    onMutate: async ({ productId, currentlyInWishlist }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.wishlist.ids });
      const previous = queryClient.getQueryData<Set<string>>(queryKeys.wishlist.ids);

      queryClient.setQueryData<Set<string>>(queryKeys.wishlist.ids, (current) => {
        const next = new Set(current ?? []);
        if (currentlyInWishlist) {
          next.delete(productId);
        } else {
          next.add(productId);
        }
        return next;
      });

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.wishlist.ids, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.wishlist.ids });
    },
  });

  const isInWishlist = useCallback(
    (productId: string) => wishlistIds.has(productId),
    [wishlistIds]
  );

  const toggleWishlist = useCallback(
    async (productId: string) => {
      if (status !== "authenticated") {
        return { ok: false, inWishlist: false, unauthorized: true };
      }

      const currentlyInWishlist = wishlistIds.has(productId);

      const result = await toggleWishlistMutation.mutateAsync({
        productId,
        currentlyInWishlist,
      });

      window.dispatchEvent(new Event("wishlist-updated"));
      queryClient.invalidateQueries({ queryKey: queryKeys.wishlist.items });

      return result;
    },
    [status, wishlistIds, toggleWishlistMutation]
  );

  const value = useMemo(
    () => ({
      isInWishlist,
      count: wishlistIds.size,
      loading: wishlistQuery.isLoading || toggleWishlistMutation.isPending,
      toggleWishlist,
    }),
    [isInWishlist, wishlistIds.size, wishlistQuery.isLoading, toggleWishlistMutation.isPending, toggleWishlist]
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) {
    throw new Error("useWishlist must be used within WishlistProvider");
  }
  return ctx;
}
